import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { fetchGenres, fetchSongsPage } from "@/lib/songs"
import type { SongListFilters } from "@/lib/songs"

/** Loads songs 30 at a time from the Sheets-backed API, paginating as the caller scrolls. */
export function useSongs(filters: SongListFilters) {
  // 즐겨찾기만 보기인데 즐겨찾기가 하나도 없으면 서버에 물어볼 것도 없이 결과는 항상 빈 목록이다.
  const enabled = filters.ids === undefined || filters.ids.length > 0

  const query = useInfiniteQuery({
    queryKey: ["songs", filters],
    queryFn: ({ pageParam }) => fetchSongsPage(filters, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    enabled,
  })

  const songs = enabled
    ? (query.data?.pages.flatMap((page) => page.songs) ?? [])
    : []
  const total = enabled ? (query.data?.pages[0]?.total ?? 0) : 0

  return {
    songs,
    total,
    loading: enabled && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
    reload: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: enabled && query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  }
}

/** Loads genre counts + total catalog size, independent of the current search/genre filter. */
export function useGenres() {
  const query = useQuery({
    queryKey: ["genres"],
    queryFn: fetchGenres,
    staleTime: 60_000,
  })

  return {
    genres: query.data?.genres ?? [],
    total: query.data?.total ?? 0,
    loading: query.isPending,
  }
}
